/* eslint-disable no-return-assign */
import React from 'react';
import { useAtlasV2MetricByWeekId, calculatePromotionsPercentChange } from 'src/serverProxy/useAtlasV2MetricByWeekId';
import DonutChartLoading from 'src/components/Charts/Donut/DonutChartLoading';
import CircularProgressPercentage from 'src/components/common/UploadAnimation/CircularProgressPercentage';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { formatMetric, safeDivide } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2ComparableSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ComparableSplineChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { Grid } from '@mui/material';
import { ComparisonPeriod, useComparisonPeriod, useMainTimePeriod } from 'src/utils/Hooks';
import { timestampToWeekId } from 'src/utils/dateUtils';

/**
 * Shows donut chart and spline charts for the top section Promotions
 */
export const AtlasV2PromotionsTopSection = () => {
  const { id: mainTimePeriodId, startWeek } = useMainTimePeriod();
  const comparisonPeriod = useComparisonPeriod();

  const { primaryTotal: retailSales, isLoading: retailSalesLoading } = useAtlasV2MetricByWeekId({
    indexName: 'sales',
    fieldName: 'retailSales'
  });
  const {
    primaryTotal: retailSalesFromPromotions,
    isLoading: retailSalesFromPromotionsLoading,
    primarySlicedData: retailSalesFromPromotionSlicedData
  } = useAtlasV2MetricByWeekId({
    indexName: 'promotions',
    fieldName: 'retailSales'
  });

  const isLastWeek = mainTimePeriodId === '1w';

  const lastWeekRetailSalesFromPromotions =
    retailSalesFromPromotionSlicedData?.[retailSalesFromPromotionSlicedData.length - 1]?.[0] ===
      timestampToWeekId(startWeek) || 0;
  const percentage = safeDivide(
    isLastWeek ? lastWeekRetailSalesFromPromotions : retailSalesFromPromotions,
    retailSales,
    0
  );

  return (
    <BeaconChartWithLegend title="Promotion Count" includeSummaryInfo showExportMenu={false}>
      <Grid display="grid" gridTemplateColumns="308px 655px" justifyContent="space-between" alignItems="center">
        <div style={{ width: '308px', height: '308px' }}>
          {retailSalesLoading || retailSalesFromPromotionsLoading ? (
            <DonutChartLoading />
          ) : (
            <CircularProgressPercentage
              percentage={percentage}
              customLabel={
                <Flex gap="sm" alignItems="center" flexDirection="column">
                  <Text
                    sx={{
                      fontSize: '48px',
                      fontWeight: 'bold'
                    }}
                  >
                    {`${formatMetric(percentage, METRICTYPE.PERCENT, {})}`}
                  </Text>
                  <Text
                    sx={{
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}
                  >
                    Share of Total Sales
                  </Text>
                </Flex>
              }
            />
          )}
        </div>
        <div style={{ flex: 1 }}>
          <AtlasV2ComparableSplineChart
            indexName="promotions"
            fieldName="dataPointCount"
            showTitleSummary={false}
            splineChartLoadingProps={{
              width: 664
            }}
            chartOptionsOverride={{
              chart: {
                height: 280,
                width: 664
              }
            }}
            requestBuilder={(builder) => builder.addConditionRangeFilter('retailSales', 0.1)}
            shouldSliceByWeekRange
            beaconChartWithLegendOverride={(props, values) => {
              // V2 format uses primaryData/comparisonData with {weekId, value} objects
              const { primaryData, comparisonData, comparisonTotal } = values;

              // Guard against undefined data
              if (!primaryData?.length || !comparisonData?.length) {
                return props;
              }

              const lastPrimaryValue = primaryData[primaryData.length - 1];
              const lastSecondaryValue = comparisonData[comparisonData.length - 1];
              const primaryValueSum =
                mainTimePeriodId === '1w'
                  ? (lastPrimaryValue?.value ?? 0)
                  : primaryData.reduce((acc, curr) => acc + (curr.value ?? 0), 0);

              // Convert V2 format to V1 format for the utility function
              const primarySlicedData = primaryData.map((d) => [d.weekId, d.value ?? 0] as [number, number]);
              const secondarySlicedData = comparisonData.map((d) => [d.weekId, d.value ?? 0] as [number, number]);

              // Use the common utility function for consistency
              const percentChangeValue = calculatePromotionsPercentChange(
                primarySlicedData,
                secondarySlicedData,
                comparisonPeriod,
                comparisonTotal,
                mainTimePeriodId
              );

              let secondaryValueSum: number;

              if (mainTimePeriodId === '1w') {
                secondaryValueSum = lastSecondaryValue?.value ?? 0;
              } else if (comparisonPeriod === ComparisonPeriod.PRIOR_YEAR) {
                secondaryValueSum = comparisonData
                  .filter((secondaryElement) =>
                    primaryData.some((primaryElement) => primaryElement.weekId === secondaryElement.weekId)
                  )
                  .reduce((acc, curr) => acc + (curr.value ?? 0), 0);
              } else {
                secondaryValueSum = comparisonTotal;
              }

              const propsOverride = {
                ...props,
                primaryLegendProps: {
                  ...props.primaryLegendProps,
                  metric: `${formatMetric(primaryValueSum, METRICTYPE.VOLUME, {
                    decimalPlaces: 2,
                    showFullValue: false
                  })}`,
                  change: formatMetric(Math.abs(percentChangeValue), METRICTYPE.PERCENT, {
                    showFullValue: true
                  })
                },
                comparisonLegendProps: {
                  ...props.comparisonLegendProps,
                  metric: `${formatMetric(secondaryValueSum, METRICTYPE.VOLUME, {
                    decimalPlaces: 2,
                    showFullValue: false
                  })}`
                }
              };

              return propsOverride;
            }}
          />
        </div>
      </Grid>
    </BeaconChartWithLegend>
  );
};
